import { Plugin } from '@tiptap/pm/state'
import Highlight from '@tiptap/extension-highlight'

export const Marker = Highlight.extend({
  name: 'slash-command-pill',
  inclusive: false,

  addAttributes() {
    if (!this.options.multicolor) {
      return {}
    }

    return {
      'data-type': {
        default: null,
      },
      color: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute('data-color') || element.style.backgroundColor,
        renderHTML: (attributes) => {
          if (!attributes.color) {
            return {}
          }

          return {
            'data-color': attributes.color,
            style: `background-color: ${attributes.color}; color: inherit`,
          }
        },
      },
    }
  },
  // Docs: https://tiptap.dev/docs/editor/extensions/custom-extensions/extend-existing#using-getattrs
  parseHTML() {
    return [
      {
        tag: 'mark',
        getAttrs: (element) =>
          element.classList.contains('slash-command-pill') ? {} : false,
      },
    ]
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handleClick(view, pos) {
            const node = view.state.doc.nodeAt(pos)
            if (!node) return false

            const highlightMark = node.marks.find(
              (mark) => mark.type.name === 'slash-command-pill',
            )

            if (highlightMark) {
              const { href, color } = highlightMark.attrs
              if (href) {
                const targetUrl = new URL(href)

                if (window.location.pathname !== targetUrl.pathname) {
                  window.location.href = href
                } else {
                  const el = document.getElementById(targetUrl.hash.slice(1))
                  if (el) {
                    el.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                      inline: 'nearest',
                    })

                    el.style.transition = 'background-color 0.5s ease-in-out'
                    el.style.backgroundColor = color ?? 'yellow'
                    setTimeout(() => {
                      el.style.backgroundColor = ''
                      el.style.border = ''
                    }, 2000)
                  }
                }

                return true
              }
            }

            return false
          },

          handleTextInput(view, from, to) {
            return isInsideSlashCommandPill(view.state, from, to)
          },

          handlePaste(view) {
            const { from, to } = view.state.selection
            return isInsideSlashCommandPill(view.state, from, to)
          },

          handleDOMEvents: {
            beforeinput(view) {
              const selection = view.state.selection
              return isInsideSlashCommandPill(
                view.state,
                selection.from,
                selection.to,
              )
            },
          },
        },

        appendTransaction(transactions, oldState, newState) {
          let tr = newState.tr
          let markDeleted = false
          const positionsToDelete: {
            from: number
            to: number
            markType: any
          }[] = []

          transactions.forEach((transaction) => {
            if (!transaction.docChanged) return

            newState.doc.descendants((node, pos) => {
              if (!node.isText) return

              const highlightMark = node.marks.find(
                (mark) => mark.type.name === 'slash-command-pill',
              )
              if (!highlightMark) return

              if (pos >= oldState.doc.content.size) return
              const oldNode = oldState.doc.nodeAt(pos)
              if (!oldNode?.isText) return

              const oldMark = oldNode.marks.find(
                (mark) => mark.type.name === 'slash-command-pill',
              )
              if (!oldMark) return

              const oldText = oldNode.text ?? ''
              const newText = node.text ?? ''
              const oldContent = oldText.includes(':')
                ? oldText.trim().split(':')[1] || ''
                : oldText
              const newContent = newText

              if (newContent.length < oldContent.length) {
                const from = pos
                const to = from + newText.length
                if (to <= newState.doc.content.size && from >= 0 && to > from) {
                  positionsToDelete.push({
                    from,
                    to,
                    markType: highlightMark.type,
                  })
                }
              }
            })
          })

          for (const { from, to, markType } of positionsToDelete) {
            try {
              tr = tr.removeMark(from, to, markType)
              tr = tr.delete(from, to)
              markDeleted = true
            } catch (e) {
              console.warn('Safe deletion failed:', { from, to }, e)
            }
          }

          return markDeleted ? tr : null
        },
      }),
    ]
  },
})

function isInsideSlashCommandPill(state, from, to) {
  let blocked = false

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isText) return

    const pillMark = node.marks.find(
      (mark) => mark.type.name === 'slash-command-pill',
    )
    if (pillMark) {
      const nodeStart = pos
      const nodeEnd = pos + (node.text?.length || 0)

      if (from > nodeStart && to < nodeEnd) {
        blocked = true
      }
    }
  })

  return blocked
}
